import React, { useState } from 'react';
import {
  DIAS_LETIVOS,
  SITUACOES_GRADE,
  aulasNoDia,
  situacaoDaGrade,
} from '../utils/situacaoGrade';

/**
 * Situação da grade horária das turmas, no Acompanhamento de Diários.
 *
 * A coordenação queria saber quais turmas já têm o horário preenchido sem
 * abrir uma por uma. O Acompanhamento já avisava das "turmas sem grade",
 * mas deduzia isso das aulas previstas no mês — em mês de recesso a rede
 * inteira aparecia sem grade, e a grade pela metade passava como pronta.
 * Aqui a conta sai da grade vigente (`/class-schedule/summary/`) e não
 * depende do mês escolhido; escola e turma do filtro valem.
 *
 * Os três totais abrem a lista das turmas daquela situação, com as aulas
 * de cada dia e o atalho para a grade.
 */
function SituacaoGradePainel({ turmas, resumo, darkMode, onAbrirGrade }) {
  const [aberta, setAberta] = useState('');

  const card = darkMode
    ? 'bg-slate-800/50 border border-slate-700/10'
    : 'bg-white/80 border border-slate-200 shadow-sm';
  const muted = darkMode ? 'text-slate-400' : 'text-slate-500';
  const strong = darkMode ? 'text-slate-100' : 'text-slate-800';

  const totais = { completa: 0, incompleta: 0, vazia: 0 };
  const porSituacao = { completa: [], incompleta: [], vazia: [] };
  turmas.forEach(t => {
    const sit = situacaoDaGrade(resumo[t.id]);
    totais[sit] += 1;
    porSituacao[sit].push(t);
  });

  const lista = aberta ? porSituacao[aberta] : [];

  return (
    <section className={`rounded-xl p-4 mb-6 ${card}`} aria-labelledby="situacao-grade-titulo">
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 mb-3">
        <h2 id="situacao-grade-titulo" className={`text-sm font-semibold ${strong}`}>
          Grade horária das turmas
        </h2>
        <p className={`text-xs ${muted}`}>
          Situação de hoje — não muda com o mês escolhido.
        </p>
      </div>

      <div className="grid grid-cols-3 gap-3">
        {Object.entries(SITUACOES_GRADE).map(([chave, sit]) => {
          const ativo = aberta === chave;
          return (
            <button
              key={chave}
              type="button"
              title={sit.dica}
              aria-expanded={ativo}
              onClick={() => setAberta(ativo ? '' : chave)}
              className={`flex flex-col justify-start text-left p-3 rounded-lg border transition-all ${
                ativo
                  ? 'border-[#0974BA] ring-2 ring-[#0974BA]/30'
                  : darkMode ? 'border-slate-700 hover:border-slate-500' : 'border-slate-200 hover:border-slate-300'
              }`}
            >
              <span className={`text-2xl font-bold tabular-nums ${sit.numero}`}>{totais[chave]}</span>
              <span className={`text-xs mt-0.5 ${muted}`}>{sit.rotulo}</span>
            </button>
          );
        })}
      </div>

      {!aberta && (
        <p className={`text-xs mt-2 ${muted}`}>Clique num total para ver as turmas.</p>
      )}

      {aberta && (
        <div className="mt-4">
          {aberta === 'vazia' && lista.length > 0 && (
            <p className={`text-xs mb-3 ${muted}`}>
              Sem a grade não é possível saber quais aulas eram esperadas — essas turmas
              ficam fora da cobrança de diários.
            </p>
          )}
          {lista.length === 0 ? (
            <p className={`text-sm py-4 text-center ${muted}`}>Nenhuma turma nesta situação.</p>
          ) : (
            <div className={`overflow-x-auto max-h-[50vh] overflow-y-auto rounded-lg border ${
              darkMode ? 'border-slate-700' : 'border-slate-200'
            }`}>
              <table className="w-full text-sm">
                <thead className={`sticky top-0 ${darkMode ? 'bg-slate-700' : 'bg-slate-100'}`}>
                  <tr className={darkMode ? 'text-slate-300' : 'text-slate-600'}>
                    <th className="text-left px-3 py-2">Turma</th>
                    <th className="text-left px-3 py-2">Escola</th>
                    {aberta !== 'vazia' && DIAS_LETIVOS.map(d => (
                      <th key={d.weekday} className="text-center px-1.5 py-2 font-medium">{d.sigla}</th>
                    ))}
                    {aberta !== 'vazia' && <th className="text-center px-3 py-2">Sem prof.</th>}
                    <th className="px-3 py-2" aria-label="Ações" />
                  </tr>
                </thead>
                <tbody className={`divide-y ${darkMode ? 'divide-slate-700' : 'divide-slate-200'}`}>
                  {lista.map(t => {
                    const linha = resumo[t.id];
                    const semProf = linha?.slots_without_teacher || 0;
                    return (
                      <tr key={t.id} className={darkMode ? 'hover:bg-slate-700/40' : 'hover:bg-slate-50'}>
                        <td className={`px-3 py-2 font-medium ${strong}`}>{t.class_name}</td>
                        <td className={`px-3 py-2 ${muted}`}>{t.school_name || '—'}</td>
                        {aberta !== 'vazia' && DIAS_LETIVOS.map(d => {
                          const n = aulasNoDia(linha, d.weekday);
                          return (
                            <td
                              key={d.weekday}
                              className={`text-center px-1.5 py-2 tabular-nums font-semibold ${
                                n > 0 ? strong : 'text-[#E83A3C]'
                              }`}
                            >
                              {n}
                            </td>
                          );
                        })}
                        {aberta !== 'vazia' && (
                          <td className={`text-center px-3 py-2 tabular-nums ${
                            semProf > 0 ? 'text-[#F9B047] font-semibold' : muted
                          }`}>
                            {semProf || '—'}
                          </td>
                        )}
                        <td className="px-3 py-2 text-right whitespace-nowrap">
                          <button
                            type="button"
                            onClick={() => onAbrirGrade(t.id)}
                            className="px-3 py-1.5 rounded-lg text-xs font-medium bg-[#0974BA]/15 text-[#0974BA] hover:bg-[#0974BA]/25"
                          >
                            {aberta === 'vazia' ? 'Montar grade' : 'Ver grade'}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

export default SituacaoGradePainel;
